import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { StandardModule } from '@modules/standard/src/StandardModule';
import zams from '@zams/logger/zed';
import { useCallback, useEffect, useRef, useState } from 'react';

import { onTreeLoadFailed } from '../observable/TourDanjiObservable';
import { getTourDanjiZamsParams, TourDanjiModule } from '../TourDanjiModule';

import treeIcon from './assets/tree_icon.svg';
import { useScreen } from './hooks/useScreen';
import { useToast } from './hooks/useToast';
import styles from './styles/TreePanel.module.css';

/** Delay after camera arrives before showing the button (ms) */
const CAMERA_SETTLE_DELAY_MS = 300;

export function TreePanel() {
  const { isDesktop } = useScreen();
  const { toast } = useToast();
  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();
  const [isTreeVisible, setIsTreeVisible] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isInteriorView, setIsInteriorView] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toggleGenRef = useRef(0);

  const showErrorBriefly = useCallback(() => {
    setIsError(true);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setIsError(false), 3000);
  }, []);

  useEffect(() => {
    if (!zed?.api) return;

    // Set initial value
    setIsTreeVisible(zed.api.getDefaultTreeVisibility());

    // Subscribe to load completion to get the final value after applyGraphicSettings
    const loadObserver = zed.api.observer.onLoadCompleted.addOnce(() => {
      setIsTreeVisible(zed.api.getDefaultTreeVisibility());
    });

    // Subscribe to tree load failures — auto-toggle off and show error
    const failObserver = onTreeLoadFailed.add(() => {
      setIsTreeVisible(false);
      setIsLoading(false);
      showErrorBriefly();
    });

    return () => {
      loadObserver?.remove();
      failObserver?.remove();
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [zed, showErrorBriefly]);

  // Fade out immediately when camera starts moving
  useEffect(() => {
    if (!zed) return;
    const observer = zed.api.observer.onUnitViewStateChanged.add(() => {
      setIsTransitioning(true);
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    });
    return () => {
      observer?.remove();
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    };
  }, [zed]);

  // Reposition and fade in after camera arrives
  useEffect(() => {
    if (!zed) return;
    const observer = zed.api.observer.onCameraViewTypeChanged.add((data) => {
      const nextInterior = ['UnitWindowView', 'FakeUnitWindowView'].includes(data);
      setIsInteriorView(nextInterior);

      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);

      if (data === 'Transition') {
        setIsTransitioning(true);
      } else {
        transitionTimerRef.current = setTimeout(() => {
          setIsTransitioning(false);
        }, CAMERA_SETTLE_DELAY_MS);
      }
    });
    return () => {
      observer?.remove();
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    };
  }, [zed]);

  const handleToggle = async () => {
    if (!zed) return;

    // Button is disabled during loading, but guard just in case
    if (isLoading) return;

    const gen = ++toggleGenRef.current;
    setIsError(false);

    const newVisibility = !isTreeVisible;
    const needsLoading = newVisibility && !zed.api.isTreeSystemLoaded();

    const toastMsg = newVisibility ? '단지 내 조경이 표시됩니다.' : '단지 내 조경이 숨김 처리됩니다.';
    toast(toastMsg);

    zams
      .z156({
        ...getTourDanjiZamsParams(),
        status: newVisibility ? '1' : '0',
      })
      .send();

    if (needsLoading) {
      setIsLoading(true);
      // Wait for React paint to complete before starting blocking work.
      // Double rAF ensures: 1st rAF → browser computes frame, 2nd rAF → paint committed.
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    }

    try {
      await zed.api.setTreeVisible(newVisibility);
      if (gen === toggleGenRef.current) {
        setIsTreeVisible(newVisibility);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error(`[TreePanel] Failed to toggle tree visibility:`, error);
    } finally {
      if (needsLoading && gen === toggleGenRef.current) {
        setIsLoading(false);
      }
    }
  };

  const toggleButtonStyle = isInteriorView
    ? isDesktop
      ? styles.toggleButtonInteriorWWW
      : styles.toggleButtonInterior
    : isDesktop
      ? styles.toggleButtonWWW
      : styles.toggleButton;

  return (
    <div style={{ zIndex: Layers.UI_CONTENTS + 1 }} className={styles.root}>
      <button
        className={`${toggleButtonStyle} ${isError ? styles.error : ''} ${
          isLoading ? '' : isTransitioning ? styles.fadeOut : styles.fadeIn
        }`}
        onClick={handleToggle}
        disabled={isLoading || isTransitioning}>
        {isTreeVisible && !isLoading && <div className={styles.toggleOn}></div>}
        <div className={styles.iconContainer}>
          <img src={treeIcon} className={styles.icon} alt="Tree icon" />
          <div className={styles.iconText}>{isError ? '실패' : '조경'}</div>
        </div>
      </button>
    </div>
  );
}
